/**
 * Runs rules on events. Used to decide if a user's behavior satisfies a rule.
 *
 * Grammar:
 * ```
 * BEHAVIORAL_CONDITION := {
 *   where: CONDITION_EXPRESSION
 *   ((, groupBy: GROUP_SPEC)
 *    , aggregate: AGGREGATE_SPEC)
 *   (, having: CONDITION_EXPRESSION)
 *   (, orderBy: ORDER_SPEC)
 *   (, select: [ OPERANDS ])
 *   (, offset: number),
 *   (, limit: number),
 *   (, from: BEHAVIORAL_CONDITION),
 * }
 *
 * CONDITION_EXPRESSION := ['and'|'or'|'not', EXPRESSION(, EXPRESSION)*]
 * EXPRESSION := CONDITION_EXPRESSION | OPERANDS
 * OPERANDS := OPERAND(, OPERAND)*
 * OPERAND := LEAF_OPERAND | RECURSIVE_OPERAND
 * GROUP_SPEC := [ FIELD_SPEC(, FIELD_SPEC)*]
 * AGGREGATE_SPEC := [ AGGREGATED_FIELD(, AGGREGATED_FIELD)* ]
 * AGGREGATED_FIELD := {
 *   op: AGGREGATE_OPERATOR,
 *   args: [ FIELD_SPEC ]
 * }
 * ORDER_SPEC := [ ORDER_FIELD(, ORDER_FIELD)* ]
 * ORDER_FIELD := {
 *   FIELD_KEY_VALUE
 *   (, direction: 'ASC' | 'DESC')
 * }
 *
 * LEAF_OPERAND := FIELD_SPEC | EVAL_SPEC | VALUE_SPEC
 * FIELD_SPEC := { FIELD_KEY_VALUE }
 * FIELD_KEY_VALUE := field: [LITERAL(, LITERAL)*]
 * EVAL_SPEC := { eval: DEFINED_FUNCTION }
 * VALUE_SPEC := { value: LITERAL | [LITERAL(, LITERAL)*] }
 * LITERAL := number | string | boolean
 *
 * RECURSIVE_OPERAND := {
 *   // AGGREGATE_OPERATOR allowed only in 'having' clause.
 *   op: (NEGATOR | COMPARATOR | ARITHMETIC_OPERATOR | AGGREGATE_OPERATOR),
 *   args: [ OPERANDS ]
 * }
 * NEGATOR := 'not'
 * COMPARATOR := 'eq' | 'is' | 'lt' | 'gt' | 'lte' | 'gte' | 'in' | 'between' | 'contains' | 'regex' | 'and' | 'or' | 'exists'
 * ARITHMETIC_OPERATOR := '+' | '-' | '*' | '/' | '%'
 * AGGREGATE_OPERATOR := 'sum' | 'count' | 'max' | 'min' | 'avg' | 'perc'
 * DEFINED_FUNCTION := 'now'
 * ```
 *
 * Examples:
 * 1. Visitors who purchased jeans.
 * ```
 * {
 *   where: ['and',
 *       ['or',
 *         {
 *           op: 'eq',
 *           args: [{
 *             field: ['metadata', 'type']
 *           }, {
 *             value: 'jeans'
 *           }]
 *         }
 *       ]
 *     ]
 * }
 * ```
 *
 * 2. Visitors who clicked 'purchase' on type jeans with total revenue at least $100 in the last 2 weeks.
 * ```
 * {
 *   where: ['and',
 *             ['or',
 *                {
 *                  op: 'eq',
 *                  args: [ {field: ['name']}, {value: 'purchase'} ]
 *                }
 *             ],
 *             ['or',
 *                {
 *                  op: 'eq',
 *                  args: [ {field: ['metadata', 'type']}, {value: 'jeans'} ]
 *                }
 *             ],
 *             ['or',
 *                {
 *                  op: 'lt',
 *                  args: [{
 *                    op: '-',
 *                    args: [ {eval: 'now'}, {field: ['timestamp']} ]
 *                   },
 *                   {value: 1209600000}]
 *                }
 *             ]
 *          ],
 *   having: ['and',
 *              ['or',
 *                 {
 *                   op: 'gt',
 *                   args: [
 *                     {
 *                       op: 'sum',
 *                       args: [ {field: ['revenue']} ]
 *                     },
 *                     {value: 100}
 *                   ]
 *                 }
 *              ]
 *           ]
 * }
 * ```
 *
 * Ported from [`rules@14f256e`](https://github.com/optimizely/optimizely/blob/14f256e247c77a1b8ddff6c0ce7d543012067c53/src/www/js/client/js)
 * @module
 */

var _ = require('optimizely-client-fn');
var enums = require('core/constants/enums');
var Logger = require('core/utils/logger');
var log = _.bind(Logger.log, Logger);
var DateEnv = require('core/env/date');
var getFieldValue = require('core/utils/key_path').getFieldValue;
var JSONEnv = require('core/lib/json');

/**
 * Returns field value by walking down key parts.
 * @param {Object.<*>} event Event to get value from.
 * @param {Array.<string>} field Key path (E.g. [ 'meta', 'purchase', 'name'], [ 'content', 'category'])
 * @param {*} [defaultValue] Default value to return if field not a key path, or if following the
 *        key path led to a nonexistent field.
 * @return {*} Value of field or undefined.
 * @private
 */
var getValueOrDefault = function(event:any, field:string[], defaultValue?:any): any {
  if (event.getValueOrDefault) {
    return event.getValueOrDefault(field, defaultValue);
  }

  if (!_.isArray(field)) {
    return defaultValue;
  }

  var value = getFieldValue(event, field);
  if (typeof value === 'undefined') {
    value = defaultValue;
  }
  return value;
};

/**
 * Returns argument after trimming and lower casing, if string.
 * @param {*} arg
 * @returns {*}
 * @private
 */
var maybeTrimLowerCase = function(arg:any): any {
  if (typeof arg === 'string') {
    return arg.trim().toLowerCase();
  }
  return arg;
};

/**
 * Rule clauses.
 * @enum {string}
 * @private
 */
exports.clause = {
  WHERE: 'where',
  GROUP_BY: 'groupBy',
  AGGREGATE: 'aggregate',
  HAVING: 'having',
  ORDER_BY: 'orderBy',
  SELECT: 'select',
  OFFSET: 'offset',
  LIMIT: 'limit',
  FROM: 'from'
};

/**
 * @callback OperatorFn
 * @param {*} arg1
 * @param {*} [arg2]
 * @returns {boolean|number}
 */

/**
 * Default field.
 */
exports.DEFAULT_FIELD = ['*'];

/**
 * Map of operator name to boolean operator
 * @type {Object.<module:feature_behavior/helpers/rules~OperatorFn>}
 */
exports.booleanOperators = {
  'eq':
    /**
     * Checks for case-insensitive, whitespace trimmed equality.
     * @param {Array.<string>} args
     * @returns {boolean}
     */
    function(args) {
      var trimmedLowerCasedArgs = _.map(args, maybeTrimLowerCase);
      return trimmedLowerCasedArgs[0] == trimmedLowerCasedArgs[1]; // eslint-disable-line eqeqeq
    },
  'is':
    /**
     * Checks for strict equality.
     * @param {Array.<number|string>} args
     * @returns {boolean}
     */
    function(args) {
      return args[0] === args[1];
    },
  'gt':
    /**
     * Checks if greater than.
     * @param {Array.<number|string>} args
     * @returns {boolean}
     */
    function(args) {
      return args[0] > args[1];
    },
  'lt':
    /**
     * Checks if lesser than.
     * @param {Array.<number|string>} args
     * @returns {boolean}
     */
    function(args) {
      return args[0] < args[1];
    },
  'gte':
    /**
     * Checks if greater than or equal.
     * @param {Array.<number|string>} args
     * @returns {boolean}
     */
    function(args) {
      return args[0] >= args[1];
    },
  'lte':
    /**
     * Checks if lesser than or equal.
     * @param {Array.<number|string>} args
     * @returns {boolean}
     */
    function(args) {
      return args[0] <= args[1];
    },
  'in':
    /**
     * Checks if element (args[0]) is present in array (args[1]).
     * @param {Array.<Array.<*>>} args
     * @returns {boolean}
     */
    function(args) {
      var trimmedLowerCasedArray = _.map(args[1] || [], maybeTrimLowerCase);
      return _.includes(trimmedLowerCasedArray, maybeTrimLowerCase(args[0]));
    },
  'between':
    /**
     * Checks if element (args[0]) is in between bounds [args[1][0], args[1][1]].
     * @param {Array.<*>} args
     * @returns {boolean}
     */
    function(args) {
      return args[1][0] <= args[0] && args[0] <= args[1][1];
    },
  'contains':
    /**
     * Checks if args[0] contains args[1].
     * @param {Array.<string>} args
     * @returns {boolean}
     */
    function(args) {
      var lowerCasedArgs = _.map(args, function(arg) {
        if (typeof arg === 'string') {
          return arg.toLowerCase();
        }
        return arg;
      });
      return (lowerCasedArgs[0] || '').indexOf(lowerCasedArgs[1]) !== -1;
    },
  'regex':
    /**
     * Checks if args[0] matches regex args[1][0] with flags args[1][1].
     * @param {Array.<*>} args
     * @returns {boolean}
     */
    function(args) {
      try {
        var expr, flags;
        if (_.isString(args[1])) {
          expr = args[1];
          flags = 'i';  // Ignore case by default.
        } else {
          expr = args[1][0] || '';
          flags = args[1][1] || '';
        }
        return (new RegExp(expr, flags)).test(args[0]);
      } catch(e) {
        Logger.error('Rules', 'In operator "regex", error: ' +
          (e.message || 'invalid RegExp /' + [expr, flags].join('/')));
        return false;
      }
    },
  'exists':
    /**
     * Returns whether we're able to compute a value for args[0].
     * @param {Array.<*>} args
     * @returns {boolean}
     */
    function(args) {
      return typeof args[0] !== 'undefined';
    },
  'and':
    /**
     * Checks if all arguments are true.
     * @param {Array.<boolean>} args
     * @returns {boolean}
     */
    function(args) {
      return _.every(args, function(arg) { return arg; });
    },
  'or':
    /**
     * Checks if any arguments are true.
     * @param {Array.<boolean>} args
     * @returns {boolean}
     */
    function(args) {
      return _.some(args, function(arg) { return arg; });
    },
  'not':
    /**
     * Returns boolean not of args[0].
     * @param {Array.<boolean>} args
     * @returns {boolean}
     */
    function(args) {
      return !args[0];
    }
};

/**
 * Map of operator name to arithmetic operators
 * @type {Object.<module:feature_behavior/helpers/rules~OperatorFn>}
 */
exports.arithmeticOperators = {
  '+':
    /**
     * Returns sum.
     * @param {Array.<number>} args Operands to sum.
     * @return {number} Sum of values.
     */
    function(args) {
      return (args[0] || 0) + (args[1] || 0);
    },
  '-':
    /**
     * Returns difference.
     * @param {Array.<number>} args Operands to difference.
     * @return {number} Difference of values.
     */
    function(args) {
      return (args[0] || 0) - (args[1] || 0);
    },
  '/':
    /**
     * Returns division.
     * @param {Array.<number>} args Operands to divide.
     * @return {number} Division result.
     */
    function(args) {
      return (args[0] || 0) / (args[1] || 1);
    },
  '%':
    /**
     * Returns modulo.
     * @param {Array.<number>} args Operands to divide.
     * @return {number} Modulo.
     */
    function(args) {
      return (args[0] || 0) % (args[1] || 1);
    }
};

/**
 * @callback AggregateFn
 * @param {Array.<Array.<string>>} args Arguments to pass to function (e.g. field identifier)
 * @param {Array.<Object.<*>>} events Events.
 * @returns {number}
 */

/**
 * Map of aggregate operator name to operator function
 * @type {Object.<module:feature_behavior/helpers/rules~AggregateFn>}
 */
exports.aggregateOperators = {
  'sum':
    /**
     * Returns sum of field values.
     * @param {Array.<Array.<string>>} args Field identifier (args[0]).
     * @param {Array.<Object.<*>>} events Events.
     * @returns {number} Sum of all events field values.
     */
    function(args, events) {
      var field = args[0] || exports.DEFAULT_FIELD;
      var total = 0;
      for (var i = 0; i < events.length; i++) {
        total += getValueOrDefault(events[i], field, 0);
      }
      return total;
    },
  'avg':
    /**
     * Returns average of field values.
     * @param {Array.<Array.<string>>} args Field identifier (args[0]).
     * @param {Array.<Object.<*>>} events Events.
     * @returns {number} Average of all events field values.
     */
    function(args, events) {
      if (events.length === 0) {
        return 0;
      }
      var field = args[0] || exports.DEFAULT_FIELD;
      var total = 0;
      for (var i = 0; i < events.length; i++) {
        total += getValueOrDefault(events[i], field, 0);
      }
      return total / events.length;
    },
  'max':
    /**
     * Returns maximum of field values.
     * @param {Array.<Array.<string>>} args Field identifier (args[0]).
     * @param {Array.<Object.<*>>} events Events.
     * @returns {number} Maximum of all events field values.
     */
    function(args, events) {
      var field = args[0] || exports.DEFAULT_FIELD;
      var max = Number.NEGATIVE_INFINITY;
      for (var i = 0; i < events.length; i++) {
        max = Math.max(max, getValueOrDefault(events[i], field, Number.NEGATIVE_INFINITY));
      }
      return max;
    },
  'min':
    /**
     * Returns minimum of field values.
     * @param {Array.<Array.<string>>} args Field identifier (args[0]).
     * @param {Array.<Object.<*>>} events Events.
     * @returns {number} Minimum of all events field values.
     */
    function(args, events) {
      var field = args[0] || exports.DEFAULT_FIELD;
      var min = Number.POSITIVE_INFINITY;
      for (var i = 0; i < events.length; i++) {
        min = Math.min(min, getValueOrDefault(events[i], field, Number.POSITIVE_INFINITY));
      }
      return min;
    },
  'count':
    /**
     * Returns number of events.
     * @param {*} unusedArgs
     * @param {Array.<Object.<*>>} events Events.
     * @returns {number} Number of events.
     */
    function(unusedArgs, events) {
      return events.length;
    }
};

// Predefined functions.
var functions = {
  'now':
    /**
     * Returns current time in milliseconds.
     * @returns {number} Current time in milliseconds.
     */
    function() {
      return DateEnv.now();
    }
};

/**
 * Returns scalar operator function.
 * @param {string} op Operator name.
 * @returns {module:plugins/feature_behavior/helpers/rules~OperatorFn|null} Operator function.
 * @private
 */
var getScalarOperator = function(op:string): import('plugins/feature_behavior/helpers/rules').OperatorFn|null {
  if (op in exports.booleanOperators) {
    return exports.booleanOperators[op];
  } else if (op in exports.arithmeticOperators) {
    return exports.arithmeticOperators[op];
  }

  return null;
};

/**
 * Operates on event.
 * @param {Object.<*>} event An event.
 * @param {Object.<*>} operation Operation.
 * @returns {*} Result of operation.
 */
var operate = function(event:Record<string, any>, operation:Record<string, any>): any {
  // Leaf nodes: value, field, eval.
  if (operation.hasOwnProperty('value')) {
    return operation['value'];
  }

  if (operation.hasOwnProperty('field')) {
    // TODO if we start passing defaultValue to getValueOrDefault:
    //   Make sure the 'exists' comparator returns false when applied to a default value.
    return getValueOrDefault(event, operation['field']);
  }

  if (operation.hasOwnProperty('eval')) {
    if (!(operation['eval'] in functions)) {
      Logger.error('Rules', 'Unknown function: ' + operation['eval']);
      return;
    }
    return functions[operation['eval']]();
  }

  // operation must be of form:
  // {
  //   op: BOOLEAN_OPERATOR | ARITHMETIC_OPERATOR,
  //   args: [ ARGUMENTS ]
  // }
  if (!operation['op']) {
    Logger.error('Rules', 'No operator specified: ' + JSONEnv.stringify(operation));
    return;
  }

  var operator = getScalarOperator(operation['op']);
  if (!operator) {
    Logger.error('Rules', 'Unknown operator: ' + operation['op']);
    return;
  }

  // Recursively evaluate all arguments.
  var curriedOperate = _.partial(operate, event);
  var args = operation['args'] || [];
  var evaluatedArgs = _.map(args, function(arg) {
    return curriedOperate(arg);
  });

  return operator(evaluatedArgs, event);
};

/**
 * Groups events using rule specifying grouping fields.
 *
 * E.g. Given events = [
 *  { 'type': 'viewed', 'name': 'jeans' },
 *  { 'type': 'viewed', 'name': 'shirt' },
 *  { 'type': 'viewed', 'name': 'jeans' },
 *  { 'type': 'purchased', 'name': 'shirt' }]
 *
 * and rule to group by 'type' and 'name'
 *
 *    rule = [
 *        { 'field': ['type'] },
 *        { 'field': ['name'] }]
 *
 * groupBy will return
 *
 *    {
 *      'type=%22viewed%22&name=%22jeans%22': {
 *        'fieldValues': {
 *          'type': 'viewed',
 *          'name': 'jeans'
 *        },
 *        'events': [event[0], event[2]]
 *      },
 *      'type=%22viewed%22&name=%22shirt%22': {
 *        'fieldValues': {
 *          'type': 'viewed',
 *          'name': 'shirt'
 *        },
 *        'events': [event[1]]
 *      },
 *      'type=%22purchased%22&name=%22shirt%22': {
 *        'fieldValues': {
 *          'type': 'purchase',
 *          'name': 'shirt'
 *        },
 *        'events': [event[3]]
 *      },
 *    }
 *
 * @param {Array.<Object.<*>>} rule Rule to group events by.
 * @param {Array.<Object.<*>>} events Events to group by.
 * @returns {Object.<*>} Grouped events keyed by group field and value combinations.
 * @private
 */
var groupBy = function(rule, events) {
  var groupedEvents = {} as any;

  if (typeof rule === 'undefined' || !_.isArray(rule) || rule.length === 0) {
    // No valid groupBy specified. Group all events into a default group.
    groupedEvents['*'] = {
      fieldValues: {},
      events: events
    };
    return groupedEvents;
  }

  var groupByFields = _.map(rule, function(arg) {
    return arg['field'];
  });
  for (var i = 0; i < events.length; i++) {
    var event = events[i];
    var keyParts = [];
    var fieldValues = {} as any;
    for (var j = 0; j < groupByFields.length; j++) {
      var field = groupByFields[j];
      var value = getValueOrDefault(event, field);
      var fieldString = field.join('.');
      fieldValues[fieldString] = value;
      // Use encodeURIComponent to avoid collisions, because fieldString and value may contain = and & characters.
      keyParts.push(encodeURIComponent(fieldString) + '=' + encodeURIComponent(JSONEnv.stringify(value)));
    }
    var key = keyParts.join('&');
    if (!groupedEvents.hasOwnProperty(key)) {
      groupedEvents[key] = {
        fieldValues: fieldValues,
        events: []
      };
    }
    groupedEvents[key].events.push(event);
  }

  return groupedEvents;
};

/**
 * Generates alias for aggregated field.
 * @param {string} operator Name of aggregate operator.
 * @param {Array.<string>} field Field to operate on.
 * @returns {string} Alias for aggregated field.
 */
exports.generateAlias = function(operator:string, field:string[]): string {
  return '_' + operator + '_' + field.join('.');
};

/**
 * Aggregates grouped events using rule specifying aggregators.
 *
 * E.g. Given grouped events
 * {
 *   'type=%22purchase%22&name=%22jeans%22': {
 *      'fieldValues': {
 *        'type': 'purchase',
 *        'name': 'jeans'
 *      },
 *      'events': [{'revenue': 45.99}, {'revenue': 55.99}]
 *   },
 *   'type=%22purchase%22&name=%22shirt%22': {
 *      'fieldValues': {
 *        'type': 'purchase',
 *        'name': 'shirt'
 *      },
 *      'events': [{'revenue': 12.99}, {'revenue': 22.99}]
 *   }
 * }
 *
 * and aggregators = [
 *  {
 *    'op': 'sum',
 *    'args': [{'field': ['revenue']}]
 *  },
 *  {
 *    'op': 'max',
 *    'args': [{'field': ['revenue']}]
 *  }
 * ]
 *
 * aggregate will return
 * {
 *   'type=%22purchase%22&name=%22jeans%22': {
 *      '_sum_revenue': 101.98,
 *      '_max_revenue': 55.99
 *   },
 *   'type=%22purchase%22&name=%22shirt%22': {
 *      '_sum_revenue': 35.98,
 *      '_max_revenue': 22.99
 *   }
 * }
 *
 * @param {Array.<Object.<*>>} aggregators Rules to aggregate grouped events by.
 * @param {Object.<*>} groups Grouped events.
 * @returns {Object.<*>} Aggregated fields keyed by group keys.
 * @private
 */
var aggregate = function(aggregators, groups) {
  var aggregateFields = {} as any;
  _.each(groups, function(group, key) {
    aggregateFields[key] = {} as any;
    for (var i = 0; i < aggregators.length; i++) {
      var aggregator = aggregators[i];
      var operator = aggregator['op'];
      if (!(operator in exports.aggregateOperators)) {
        Logger.error('Rules', 'Unknown aggregate operator ' + operator);
        continue;
      }
      var field = ((aggregator['args'] && aggregator['args'][0]) || {})['field'] || exports.DEFAULT_FIELD;
      var alias = exports.generateAlias(operator, field);
      var aggregateValue = exports.aggregateOperators[operator]([field], group.events);
      aggregateFields[key][alias] = aggregateValue;
    }
  });

  return aggregateFields;
};

/**
 * Projects the groupBy fields from each grouped event, and the corresponding aggregated fields
 * from each aggregated event into a single event.
 *
 * E.g. given groupedEvents
 * {
 *   'type=%22purchase%22&name=%22jeans%22': {
 *      'fieldValues': {
 *        'type': 'purchase',
 *        'name': 'jeans'
 *      },
 *      'events': [{'revenue': 45.99}, {'revenue': 55.99}]
 *   },
 *   'type=%22purchase%22&name=%22shirt%22': {
 *      'fieldValues': {
 *        'type': 'purchase',
 *        'name': 'shirt'
 *      },
 *      'events': [{'revenue': 12.99}, {'revenue': 22.99}]
 *   }
 * }
 *
 * and aggregateEvents
 *
 * {
 *   'type=%22purchase%22&name=%22jeans%22': {
 *      '_sum_revenue': 101.98,
 *      '_max_revenue': 55.99
 *   },
 *   'type=%22purchase%22&name=%22shirt%22': {
 *      '_sum_revenue': 35.98,
 *      '_max_revenue': 22.99
 *   }
 * }
 *
 * project will return
 *
 * [
 *  {
 *    'type': 'purchase',
 *    'name': 'jeans',
 *    '_sum_revenue': 101.98,
 *    '_max_revenue': 55.99
 *  },
 *  {
 *    'type': 'purchase',
 *    'name': 'shirt',
 *    '_sum_revenue': 35.98,
 *    '_max_revenue': 22.99
 *  },
 * ]
 *
 * @param {Object.<*>} groupedEvents Grouped events containing the 'groupBy' fields.
 * @param {Object.<*>} aggregateEvents Aggregate events containing the aggregated fields.
 * @returns {Array.<Object.<*>>} Events containing the grouped and aggregated fields.
 * @private
 */
var project = function(groupedEvents:Record<string, any>, aggregateEvents:Record<string, any>): Record<string, any>[] {
  var projectedEvents = [];
  _.each(groupedEvents, function(group, key) {
    var event = _.extend({}, group.fieldValues);
    var aggregateFieldValues = aggregateEvents[key] || {};
    _.extend(event, aggregateFieldValues);
    projectedEvents.push(event);
  });
  return projectedEvents;
};

/**
 * Orders events using rule.
 *
 * E.g. Given events
 * [{
 *    a: 10, b: 'pony'
 *  }, {
 *    a: 12, b: 'johny'
 *  }, {
 *    a: 10, b: 'money'
 *  }, {
 *    a: 1, b: 'phony'
 *  }, {
 *    a: 12, b: 'sonny'
 *  }, {
 *    a: 1, b: 'calumny'
 * }]
 *
 * and rule
 * [{
 *    field: ['a'],
 *    direction: 'ASC'
 *  }, {
 *    field: ['b'],
 *    direction: 'DESC'
 * }]
 *
 * returns
 * [{
 *    a: 1, b: 'phony'
 *  }, {
 *    a: 1, b: 'calumny'
 *  }, {
 *    a: 10, b: 'pony'
 *  }, {
 *    a: 10, b: 'money'
 *  }, {
 *    a: 12, b: 'sonny'
 *  }, {
 *    a: 12, b: 'johny'
 * }]
 *
 * @param {Array.<Object.<*>>} rule Rule to order events by.
 * @param {Array.<Object.<*>>} events Events to order.
 * @returns {Array.<Object.<*>>} Ordered events.
 * @private
 */
var orderBy = function(rule:Record<string, any>[], events:Record<string, any>[]): Record<string, any>[] {
  if (!_.isArray(rule)) {
    log('Rules', 'groupBy rule must be an array');
    return events;
  }

  if (rule.length === 0) {
    return events;
  }

  return events.sort(function(a, b) {
    for (var i = 0; i < rule.length; i++) {
      var sortBy = rule[i];
      // Assume ascending order by default.
      var sortDirection = sortBy['direction'] || 'ASC';
      var direction = (sortDirection === 'ASC' ? 1 : -1);
      var sortField = sortBy['field'];
      var valueA = getValueOrDefault(a, sortField, 0);
      var valueB = getValueOrDefault(b, sortField, 0);
      if (valueA < valueB) {
        return -direction;
      } else if (valueA > valueB) {
        return direction;
      }
    }
    return 0;
  });
};


/**
 * Rewrites rule so that the WHERE and HAVING clauses have the standard form:
 * {
 *   op: <operation>,
 *   args: <Array of arguments>
 * }
 * and extracts all aggregations into a separate AGGREGATE clause and uses the
 * aggregated fields in the HAVING clause.
 *
 * E.g. rule {
 * 'where': ['and', ['or', [A]], ['or', [B]]]
 * 'having': ['and',
 *            ['or',
 *               {
 *                 op: 'gt',
 *                 args: [
 *                   {
 *                     op: 'sum',
 *                     args: [ { field: ['revenue'] } ]
 *                   },
 *                   {value: 100}
 *                 ]
 *               }
 *            ]
 *         ]
 ** }
 *
 * will be rewritten to
 * {
 *   'where': {
 *     op: 'and',
 *     args: [
 *       {
 *         op: 'or',
 *         args: [A]
 *       }, {
 *         op: 'or',
 *         args: [B]
 *       }
 *     ]
 *   }
 *   'aggregate': [{
 *     op: 'sum',
 *     args: [
 *       { field: ['revenue'] }
 *     ]
 *    }]
 *   'having': {
 *     op: 'and',
 *     args: [
 *       {
 *         op: 'or',
 *         args: [
 *           {
 *             op: 'gt',
 *             args: [
 *               { field: ['_sum_revenue'] },
 *               { value: 100 }
 *             ]
 *           }
 *         ]
 *       }
 *     ]
 *   }
 * }
 * @param {Object.<*>} rule Rule to rewrite.
 * @returns {RuleSpec} Rewritten rule.
 */
exports.rewrite = function(rule:Record<string, any>): RuleSpec {
  var aggregators = [];
  var aggregatorsSeen = {} as any;  // Used to de-dupe aggregators.

  function rewriteRecursively(operation, collectAggregators) {
    // Rewrite 'and', 'or'.
    if (_.isArray(operation)) {
      if (operation[0] !== 'and' && operation[0] !== 'or' && operation[0] !== 'not') {
        Logger.error('Rules', 'Unexpected operation ' + operation[0] + '. Continuing optimistically.');
      }
      operation = {
        'op': operation[0],
        'args': operation.slice(1)
      };
    }

    if (operation.hasOwnProperty('field') ||
      operation.hasOwnProperty('value') ||
      operation.hasOwnProperty('eval')) {
      return operation;
    }

    if (collectAggregators && operation['op'] in exports.aggregateOperators) {
      var field = ((operation['args'] && operation['args'][0]) || {})['field'] || exports.DEFAULT_FIELD;
      var fieldAlias = exports.generateAlias(operation['op'], field);
      if (!(fieldAlias in aggregatorsSeen)) {
        aggregators.push({
          'op': operation['op'],
          'args': operation['args']
        });
        aggregatorsSeen[fieldAlias] = true;
      }

      return {
        'field': [ fieldAlias ]
      };
    }

    var rewrittenArgs = [];
    var args = operation['args'] || [];
    for (var j = 0; j < args.length; j++) {
      rewrittenArgs[j] = rewriteRecursively(args[j], collectAggregators);
    }
    return {
      'op': operation['op'],
      'args': rewrittenArgs
    };
  }

  var rewrittenRule = {} as any;
  if (rule.hasOwnProperty(exports.clause.WHERE)) {
    rewrittenRule[exports.clause.WHERE] = rewriteRecursively(rule[exports.clause.WHERE], false);
  }
  if (rule.hasOwnProperty(exports.clause.HAVING)) {
    rewrittenRule[exports.clause.HAVING] = rewriteRecursively(rule[exports.clause.HAVING], true);
  }
  if (rule.hasOwnProperty(exports.clause.AGGREGATE) || aggregators.length > 0) {
    rewrittenRule[exports.clause.AGGREGATE] = (rule[exports.clause.AGGREGATE] || []).concat(aggregators);
  }

  var copyOver = [
    exports.clause.GROUP_BY,
    exports.clause.ORDER_BY,
    exports.clause.SELECT,
    exports.clause.OFFSET,
    exports.clause.LIMIT,
  ];
  for (var i = 0; i < copyOver.length; i++) {
    if (rule.hasOwnProperty(copyOver[i])) {
      rewrittenRule[copyOver[i]] = rule[copyOver[i]];
    }
  }

  if (rule.hasOwnProperty(exports.clause.FROM)) {
    rewrittenRule[exports.clause.FROM] = exports.rewrite(rule[exports.clause.FROM]);
  }

  return rewrittenRule;
};

/**
 * Verifies that rule has required clauses and correct operators.
 * PRE: Rule must be in standard form. Use rewrite to obtain standard form.
 * @param {RuleSpec} rule Rule to verify.
 * @param {number} [level] Number used to track recursive verification.
 * @returns {Array.<string>} List of possible violations.
 * @private
 */
var verify = function(rule:RuleSpec, level?:number): string[] {
  level = level || 0;
  var violations = [];
  if (!rule.hasOwnProperty(exports.clause.WHERE)) {
    violations.push('Missing WHERE clause');
  } else {
    // The top-level WHERE operator must be a boolean operator.
    if (!rule[exports.clause.WHERE]['op']) {
      violations.push('Missing WHERE clause operator');
    } else if (!(rule[exports.clause.WHERE]['op'] in exports.booleanOperators)) {
      violations.push('Non-boolean WHERE clause operator');
    }
  }

  // The top-level HAVING operator must be a boolean operator.
  if (rule.hasOwnProperty(exports.clause.HAVING)) {
    if (!rule[exports.clause.HAVING]['op']) {
      violations.push('Missing HAVING clause operator');
    } else if (!(rule[exports.clause.HAVING]['op'] in exports.booleanOperators)) {
      violations.push('Non-boolean HAVING clause operator');
    }
  }

  // A GROUP_BY clause must be accompanied by an aggregate clause.
  if (rule.hasOwnProperty(exports.clause.GROUP_BY) &&
    !rule.hasOwnProperty(exports.clause.AGGREGATE)) {
    violations.push('No AGGREGATE clause specified with GROUP_BY clause');
  }

  // A SELECT clause cannot have an aggregate operator.
  if (rule.hasOwnProperty(exports.clause.SELECT)) {
    var selectors = rule[exports.clause.SELECT];
    if (!_.isArray(selectors)) {
      violations.push('SELECT clause must be an array');
    } else {
      for (var i = 0; i < selectors.length; i++) {
        if (selectors[i]['op'] && (selectors[i]['op'] in exports.aggregateOperators)) {
          violations.push('In SELECT clause, aggregate operator "' +
            selectors[i]['op'] + '" specified in selector at index ' + i);
        }
      }
    }
  }

  // The OFFSET clause must specify a positive integer.
  if (rule.hasOwnProperty(exports.clause.OFFSET)) {
    var offset = rule[exports.clause.OFFSET];
    if (!_.isNumber(offset) ||
        Number(offset) < 0 ||
        Number(offset) !== Math.floor(Number(offset))) {
      violations.push('OFFSET must be a non-negative integer');
    }
  }

  // The LIMIT clause must specify a positive integer.
  if (rule.hasOwnProperty(exports.clause.LIMIT)) {
    var limit = rule[exports.clause.LIMIT];
    if (!_.isNumber(limit) ||
        Number(limit) < 0 ||
        Number(limit) !== Math.floor(Number(limit))) {
      violations.push('LIMIT must be a non-negative integer');
    }
  }

  // Add level info.
  if (level > 0) {
    violations = _.map(violations, function(violation) {
      return 'Sub-rule ' + level + ': ' + violation;
    });
  }

  if (rule.hasOwnProperty(exports.clause.FROM)) {
    violations = violations.concat(verify(rule[exports.clause.FROM], level + 1));
  }

  return violations;
};

/**
 * Selects data from events. The selected data could be field values or
 * transformed field values. A selector cannot specify an aggregate function.
 *  E.g. given selectors
 *  [
 *   {
 *     field: ['foo']
 *   },
 *   {
 *     op: '+',
 *     args: [
 *     {
 *       field: ['bar']
 *     },
 *     {
 *       value: 10
 *     }]
 *   }
 *  ]
 *  and events
 *  [
 *   {
 *     foo: 'haha',
 *     bar: 1.02
 *   },
 *   {
 *     foo: 'whaa',
 *     bar: 2.12
 *   }
 *  ]
 *  the output will be
 *  [
 *    ['haha', 11.02],
 *    ['whaa', 12.12]
 *  ]
 *
 *
 * @param {Array.<Object.<*>>} selectors List of selectors.
 * @param {Array.<Object.<*>>} events List of events.
 * @returns {Array.<*>} List of selected values.
 * @private
 */
var select = function(selectors:Record<string, any>[], events:Record<string, any>[]): any[] {
  return _.map(events, function(event) {
    return _.map(selectors, function(selector) {
      return operate(event, selector);
    });
  });
};

/**
 * Recursively execute rule on events.
 * PRE: Rule must be in standard form. Use rewrite to obtain standard form.
 * @param {RuleSpec} rule Rule to evaluate.
 * @param {Array.<Object.<*>>} events Events to evaluate rule on.
 * @returns {Array.<*>} Filtered, grouped, aggregated, and ordered events.
 * @private
 */
var executeRecursively = function(rule:RuleSpec, events:Record<string, any>[]): any[] {
  (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').time('rulesExecuteRecursively');
  if (__INVARIANT__) {
    require('core/utils/invariant').isType('RuleSpec', rule);
  }

  var resultEvents = events;
  if (rule.hasOwnProperty(exports.clause.FROM)) {
    Logger.debug('Evaluating FROM clause:', rule[exports.clause.FROM]);
    resultEvents = executeRecursively(rule[exports.clause.FROM], resultEvents);
    Logger.debug('Results after FROM:', resultEvents);
  }

  // 1. Filter.
  Logger.debug('Evaluating WHERE clause:', rule[exports.clause.WHERE]);
  resultEvents = _.filter(resultEvents, function(event) {
    return operate(event, rule[exports.clause.WHERE]);
  });
  Logger.debug('Results after WHERE:', resultEvents);

  // 2. Group by, aggregate, and project.
  if (rule.hasOwnProperty(exports.clause.AGGREGATE)) {
    Logger.debug('Evaluating AGGREGATE clause:', rule[exports.clause.AGGREGATE]);
    // If there's no GROUP_BY clause specified, groupBy returns all events as one group by default.
    var groupedEvents = groupBy(rule[exports.clause.GROUP_BY], resultEvents);
    var aggregateEvents = aggregate(rule[exports.clause.AGGREGATE], groupedEvents);
    resultEvents = project(groupedEvents, aggregateEvents);
    Logger.debug('Results after AGGREGATE:', resultEvents);
  }

  // 3. Maybe filter again?
  if (rule.hasOwnProperty(exports.clause.HAVING)) {
    Logger.debug('Evaluating HAVING clause:', rule[exports.clause.HAVING]);
    resultEvents = _.filter(resultEvents, function(event) {
      return operate(event, rule[exports.clause.HAVING]);
    });
    Logger.debug('Results after HAVING:', resultEvents);
  }

  // 4. Order.
  if (rule.hasOwnProperty(exports.clause.ORDER_BY)) {
    Logger.debug('Evaluating ORDER_BY clause:', rule[exports.clause.ORDER_BY]);
    resultEvents = orderBy(rule[exports.clause.ORDER_BY], resultEvents);
    Logger.debug('Results after ORDER_BY:', resultEvents);
  }

  // 5. Offset and limit.
  var begin = 0;
  if (rule.hasOwnProperty(exports.clause.OFFSET)) {
    Logger.debug('Evaluating OFFSET clause:', rule[exports.clause.OFFSET]);
    begin = Number(rule[exports.clause.OFFSET]);
  }
  var end;
  if (rule.hasOwnProperty(exports.clause.LIMIT)) {
    Logger.debug('Evaluating LIMIT clause:', rule[exports.clause.LIMIT]);
    end = begin + Number(rule[exports.clause.LIMIT]);
  }
  if (begin > 0 || !_.isUndefined(end)) {
    // Perform a single slice whether we have just OFFSET, just LIMIT, or both.
    resultEvents = resultEvents.slice(begin, end);
    Logger.debug('Results after OFFSET/LIMIT:', resultEvents);
  }

  // 6. Select fields.
  if (rule.hasOwnProperty(exports.clause.SELECT)) {
    Logger.debug('Evaluating SELECT clause:', rule[exports.clause.SELECT]);
    resultEvents = select(rule[exports.clause.SELECT], resultEvents);
    Logger.debug('Results after SELECT:', resultEvents);
  }

  (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').timeEnd('rulesExecuteRecursively');
  return resultEvents;
};

/**
 * Executes rule on events.
 * @param {Object.<*>} rule Rule to evaluate.
 * @param {Array.<Object.<*>>} events Events to evaluate rule on.
 * @returns {Array.<*>} Filtered, grouped, aggregated, and ordered events.
 * @throws {Error} When violations found in rule.
 */
exports.execute = function(rule:RuleSpec, events:Record<string, any>[]): any[] {
  // Rewrite WHERE and HAVING clause to standard form: { op: <operation>, args: [arguments] }.
  // Extract AGGREGATE operations into an AGGREGATE clause.
  rule = exports.rewrite(rule);

  if (Logger.shouldLog(enums.LogLevel.DEBUG)) {
    Logger.groupCollapsed('Evaluating Behavioral Rule');
  }
  Logger.debug('Rule:', rule, JSONEnv.stringify(rule));
  Logger.debug('Events:', events);

  var maybeViolations = verify(rule);
  if (maybeViolations.length > 0) {
    throw new Error('Rule ' + JSONEnv.stringify(rule) + ' has violations: ' + maybeViolations.join('\n'));
  }

  var result = executeRecursively(rule, events);
  Logger.debug('Rule result:', result);

  if (Logger.shouldLog(enums.LogLevel.DEBUG)) {
    Logger.groupEnd();
  }

  return result;
};

/**
 * Evaluates whether events satisfy rule.
 * @param {Object.<*>} rule Rule to evaluate.
 * @param {Array.<Object.<*>>} events Events to evaluate rule on.
 * @returns {boolean} Whether events satisfy rule.
 */
exports.isSatisfied = function(rule:RuleSpec, events:Record<string, any>[]): boolean {
  try {
    return exports.execute(rule, events).length > 0;
  } catch(e) {
    // TODO(ajith): Send error reporting to RUM?
    Logger.error('Rules', 'Error ' + e.toString() + ' while evaluating rule ' + JSONEnv.stringify(rule));
    return false;
  }
};

if (__TEST__) {
  exports.getValueOrDefault = getValueOrDefault;

  exports.aggregate = aggregate;
  exports.groupBy = groupBy;
  exports.operate = operate;
  exports.orderBy = orderBy;
  exports.project = project;
  exports.select = select;
  exports.verify = verify;
}
